import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { fetchSecretAudit, fetchSecretHistory, fetchSecretRequests, fetchSecrets, fetchSecretsMe } from './index';
import type { SecretRequestStatus, SecretsMe } from './types';

// Value-free lists only. Secret values never go through a query cache.
export const SECRETS_QUERY_KEYS = {
  all: ['secrets'] as const,
  list: () => ['secrets', 'list'] as const,
  history: (path: string) => ['secrets', 'history', path] as const,
  audit: (path: string) => ['secrets', 'audit', path] as const,
  requests: (status: SecretRequestStatus) => ['secrets', 'requests', status] as const,
};

export type SecretsAccess = { status: 'loading' } | { status: 'unavailable' } | { status: 'ready'; me: SecretsMe };

/**
 * Plain effect rather than useQuery so the navbar can call it outside a QueryClientProvider.
 * Any failure, including a 404 from an API deployed after this admin, reads as "no Secrets section".
 */
export const useSecretsAccess = (): SecretsAccess => {
  const [access, setAccess] = useState<SecretsAccess>({ status: 'loading' });
  useEffect(() => {
    let active = true;
    fetchSecretsMe()
      .then((me) => {
        if (active) {
          setAccess({ status: 'ready', me: { canManage: me?.canManage === true, email: me?.email ?? null } });
        }
      })
      .catch(() => {
        if (active) {
          setAccess({ status: 'unavailable' });
        }
      });
    return () => {
      active = false;
    };
  }, []);
  return access;
};

export const useSecretsList = () => useQuery({ queryKey: SECRETS_QUERY_KEYS.list(), queryFn: fetchSecrets });

export const useSecretHistory = (path: string) =>
  useQuery({ queryKey: SECRETS_QUERY_KEYS.history(path), queryFn: () => fetchSecretHistory(path) });

export const useSecretAudit = (path: string, enabled: boolean) =>
  useQuery({ queryKey: SECRETS_QUERY_KEYS.audit(path), queryFn: () => fetchSecretAudit(path), enabled });

export const useSecretRequests = (status: SecretRequestStatus) =>
  useQuery({ queryKey: SECRETS_QUERY_KEYS.requests(status), queryFn: () => fetchSecretRequests(status) });
